import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { analyticsWebsites } from "../../api/analyticsWebsites";
import { useAnalyticsScope } from "../../features/analytics/useAnalyticsScope";
import { analyticsKey } from "../../features/analytics/queryKeys";
import { parseRange, rangeParams } from "../../features/analytics/dateRange";
import {
  Empty,
  ErrorState,
  Loading,
} from "../../features/analytics/components/States";
import styles from "../../features/analytics/Analytics.module.css";
export default function LegacyAnalyticsRedirect({
  kind,
}: {
  kind: "site" | "ga4" | "gsc";
}) {
  const { id = "" } = useParams();
  const [params] = useSearchParams();
  const { scope, ready, error, retry } = useAnalyticsScope();
  const range = parseRange(params);
  const gsc =
    params.get("gsc_id") ??
    params.get("gscId") ??
    params.get("gsc") ??
    undefined;
  const query = useQuery({
    queryKey: analyticsKey(scope, "legacy", kind, id, gsc),
    queryFn: ({ signal }) => analyticsWebsites.resolve(kind, id, gsc, signal),
    enabled: ready,
    retry: false,
  });
  if (error)
    return (
      <main className={styles.root}>
        <ErrorState error={new Error(error)} retry={retry} />
      </main>
    );
  if (!ready || query.isPending)
    return (
      <main className={styles.root}>
        <Loading label="Finding this website…" />
      </main>
    );
  if (query.isError) {
    const status = (query.error as { status?: number }).status;
    return (
      <main className={styles.root}>
        <h1>Open website analytics</h1>
        {status === 404 || status === 409 ? (
          <Empty
            title={
              status === 409
                ? "Review this website’s connections"
                : "This saved link needs a website entry"
            }
            action={
              <Link
                className={styles.textLink}
                to={`/analytics?${rangeParams(range)}`}
              >
                Go to websites →
              </Link>
            }
          >
            Open the website collection and import connected websites, then
            review the exact sources. Your selected reporting period has been
            preserved.
          </Empty>
        ) : (
          <ErrorState error={query.error} retry={() => query.refetch()} />
        )}
      </main>
    );
  }
  return (
    <Navigate
      replace
      to={`/analytics/websites/${query.data.website_id}/${query.data.mapping_review_required ? "connections" : "overview"}?${rangeParams(range)}`}
    />
  );
}
